"use client";

import { type EdgeProps, getSmoothStepPath } from "@xyflow/react";
import { memo } from "react";
import type { PipelineNodeStatus } from "../types";

interface EdgeStyle {
  stroke: string;
  strokeWidth: number;
  dasharray?: string;
  animate: boolean;
}

const EDGE_STYLES: Record<PipelineNodeStatus, EdgeStyle> = {
  planned: {
    stroke: "#4b5563",
    strokeWidth: 1.5,
    dasharray: "4 4",
    animate: false,
  },
  pending: {
    stroke: "#d1d5db",
    strokeWidth: 1.5,
    dasharray: "4 4",
    animate: false,
  },
  active: {
    stroke: "#3b82f6",
    strokeWidth: 2.5,
    animate: true,
  },
  completed: {
    stroke: "#10b981",
    strokeWidth: 2,
    animate: false,
  },
  failed: {
    stroke: "#ef4444",
    strokeWidth: 2.5,
    animate: false,
  },
  aborted: {
    stroke: "#f97316",
    strokeWidth: 2,
    dasharray: "6 3",
    animate: false,
  },
  blocked: {
    stroke: "#94a3b8",
    strokeWidth: 1.5,
    dasharray: "3 5",
    animate: false,
  },
  skipped: {
    stroke: "#4b5563",
    strokeWidth: 1.5,
    dasharray: "4 4",
    animate: false,
  },
};

export const PipelineEdge = memo(function PipelineEdge(props: EdgeProps) {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    data,
  } = props;
  const status =
    ((data as Record<string, unknown> | undefined)
      ?.status as PipelineNodeStatus) ?? "planned";
  const style = EDGE_STYLES[status];
  // Sanitize ID for SVG url(#id) — spaces/special chars break marker refs
  const markerId = `arrow-${id.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  const [edgePath] = getSmoothStepPath({
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    borderRadius: 8,
  });

  return (
    <g>
      <defs>
        <marker
          id={markerId}
          markerWidth="8"
          markerHeight="8"
          viewBox="0 0 8 8"
          refX="7"
          refY="4"
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path
            d="M0 1 L7 4 L0 7"
            fill="none"
            stroke={style.stroke}
            strokeWidth="1"
          />
        </marker>
      </defs>
      {/* Wider invisible hit area */}
      <path d={edgePath} fill="none" stroke="transparent" strokeWidth={10} />
      {/* Visible edge */}
      <path
        id={id}
        d={edgePath}
        fill="none"
        stroke={style.stroke}
        strokeWidth={style.strokeWidth}
        strokeDasharray={style.dasharray}
        strokeLinecap="round"
        markerEnd={`url(#${markerId})`}
        className={style.animate ? "pipeline-edge-march" : ""}
      />
    </g>
  );
});
